import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import { usePermission } from '../hooks/usePermission';
import {
  getOrganizationUsers,
  updateUserRole,
  deactivateUser,
  reactivateUser,
  createInvitation,
  getOrganizationInvitations,
  cancelInvitation,
  InvitationData,
} from '../services/userService';
import { User, Role } from '@easydone/shared';
import {
  UserPlusIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

export function UsersPage() {
  const { userData } = useAuth();
  const { currentOrg } = useOrg();
  const canManageUsers = usePermission('users.manage');

  const [users, setUsers] = useState<User[]>([]);
  const [invitations, setInvitations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  useEffect(() => {
    loadData();
  }, [currentOrg]);

  const loadData = async () => {
    if (!currentOrg) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [usersData, invitationsData] = await Promise.all([
        getOrganizationUsers(currentOrg.id),
        getOrganizationInvitations(currentOrg.id),
      ]);
      setUsers(usersData);
      setInvitations(invitationsData);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async (email: string, role: Role) => {
    if (!currentOrg || !userData) return;

    try {
      const invitationData: InvitationData = {
        email,
        role,
        orgId: currentOrg.id,
        invitedBy: userData.id,
      };

      await createInvitation(invitationData);
      setShowInviteModal(false);
      loadData();
    } catch (error) {
      console.error('Failed to invite user:', error);
      alert('Failed to send invitation');
    }
  };

  const handleUpdateRole = async (userId: string, newRole: Role) => {
    try {
      await updateUserRole(userId, newRole);
      setEditingUser(null);
      loadData();
    } catch (error) {
      console.error('Failed to update role:', error);
      alert('Failed to update user role');
    }
  };

  const handleToggleActive = async (user: User) => {
    try {
      if (user.status === 'active') {
        await deactivateUser(user.id);
      } else {
        await reactivateUser(user.id);
      }
      loadData();
    } catch (error) {
      console.error('Failed to toggle user status:', error);
      alert('Failed to update user status');
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      await cancelInvitation(invitationId);
      loadData();
    } catch (error) {
      console.error('Failed to cancel invitation:', error);
      alert('Failed to cancel invitation');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading users...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team Members</h1>
          <p className="text-gray-600 mt-1">
            Manage your team and their roles
          </p>
        </div>
        {canManageUsers && (
          <button
            onClick={() => setShowInviteModal(true)}
            className="btn btn-primary flex items-center"
          >
            <UserPlusIcon className="w-5 h-5 mr-2" />
            Invite User
          </button>
        )}
      </div>

      {/* Pending Invitations */}
      {invitations.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-yellow-900 mb-2">
            Pending Invitations ({invitations.length})
          </h3>
          <div className="space-y-2">
            {invitations.map((invitation) => (
              <div
                key={invitation.id}
                className="flex items-center justify-between bg-white rounded px-3 py-2"
              >
                <div>
                  <span className="font-medium">{invitation.email}</span>
                  <span className="text-sm text-gray-500 ml-2">
                    → {invitation.role.replace('_', ' ')}
                  </span>
                </div>
                {canManageUsers && (
                  <button
                    onClick={() => handleCancelInvitation(invitation.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    Cancel
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Users List */}
      <div className="bg-white rounded-lg border border-gray-200">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">
                Name
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">
                Email
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">
                Role
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              {canManageUsers && (
                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center mr-3">
                      <span className="text-sm font-medium text-primary-600">
                        {user.displayName?.charAt(0).toUpperCase() || user.email.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <span className="font-medium text-gray-900">
                      {user.displayName || 'No name'}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 text-gray-600">{user.email}</td>
                <td className="px-6 py-4">
                  {editingUser?.id === user.id ? (
                    <select
                      value={editingUser.role}
                      onChange={(e) =>
                        setEditingUser({ ...editingUser, role: e.target.value as Role })
                      }
                      className="input py-1"
                    >
                      <option value={Role.ORG_ADMIN}>Org Admin</option>
                      <option value={Role.CENTRAL_ENGINEER}>Central Engineer</option>
                      <option value={Role.LOCAL_ENGINEER}>Local Engineer</option>
                      <option value={Role.TEAM_LEADER}>Team Leader</option>
                      <option value={Role.OPERATOR}>Operator</option>
                      <option value={Role.CLIENT_VIEWER}>Client Viewer</option>
                    </select>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                      {user.role.replace('_', ' ')}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : user.status === 'suspended'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                  </span>
                </td>
                {canManageUsers && (
                  <td className="px-6 py-4 text-right space-x-2">
                    {editingUser?.id === user.id ? (
                      <>
                        <button
                          onClick={() => handleUpdateRole(user.id, editingUser.role)}
                          className="text-green-600 hover:text-green-700"
                        >
                          <CheckCircleIcon className="w-5 h-5 inline" />
                        </button>
                        <button
                          onClick={() => setEditingUser(null)}
                          className="text-gray-600 hover:text-gray-700"
                        >
                          <XCircleIcon className="w-5 h-5 inline" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => setEditingUser(user)}
                          className={`${
                            user.id === userData?.id
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-primary-600 hover:text-primary-700'
                          }`}
                          disabled={user.id === userData?.id}
                          title={user.id === userData?.id ? 'Cannot edit your own account' : 'Edit role'}
                        >
                          <PencilIcon className="w-5 h-5 inline" />
                        </button>
                        <button
                          onClick={() => handleToggleActive(user)}
                          className={
                            user.id === userData?.id
                              ? 'text-gray-300 cursor-not-allowed'
                              : user.status === 'active'
                              ? 'text-yellow-600 hover:text-yellow-700'
                              : 'text-green-600 hover:text-green-700'
                          }
                          disabled={user.id === userData?.id}
                          title={user.id === userData?.id ? 'Cannot deactivate your own account' : ''}
                        >
                          {user.status === 'active' ? 'Deactivate' : 'Activate'}
                        </button>
                      </>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <InviteUserModal
          onClose={() => setShowInviteModal(false)}
          onInvite={handleInviteUser}
        />
      )}
    </div>
  );
}

interface InviteUserModalProps {
  onClose: () => void;
  onInvite: (email: string, role: Role) => Promise<void>;
}

function InviteUserModal({ onClose, onInvite }: InviteUserModalProps) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>(Role.OPERATOR);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onInvite(email, role);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h2 className="text-xl font-bold mb-4">Invite Team Member</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input w-full"
              required
            />
          </div>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              className="input w-full"
            >
              <option value={Role.ORG_ADMIN}>Org Admin</option>
              <option value={Role.CENTRAL_ENGINEER}>Central Engineer</option>
              <option value={Role.LOCAL_ENGINEER}>Local Engineer</option>
              <option value={Role.TEAM_LEADER}>Team Leader</option>
              <option value={Role.OPERATOR}>Operator</option>
              <option value={Role.CLIENT_VIEWER}>Client Viewer</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {role === Role.ORG_ADMIN && 'Full access to all features'}
              {role === Role.CENTRAL_ENGINEER && 'Can manage templates, view all inspections'}
              {role === Role.LOCAL_ENGINEER && 'Can manage assigned sites and review inspections'}
              {role === Role.TEAM_LEADER && 'Can create and review inspections'}
              {role === Role.OPERATOR && 'Can create and complete inspections'}
              {role === Role.CLIENT_VIEWER && 'Read-only access to assigned reports'}
            </p>
          </div>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
